/**
 * Dashboard Registry
 *
 * Manages the **layout and placement** of widget instances on the user's
 * dashboard — panel positions, sizes, user configuration, and persistence.
 * Communicates with the backend API for save/load of dashboard layouts.
 *
 * Relationship with Widget Registry (`widget-registry.ts`):
 * - **Widget Registry** = "what widgets exist" (type catalog / component library)
 * - **Dashboard Registry** = "where widgets are placed" (layout, panels, persistence)
 *
 * The Dashboard Registry references widget types defined in the Widget Registry
 * when instantiating and rendering panels.
 *
 * Part of Core Hardening Phase 2.
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import React from 'react';
import {
  getDashboardPanels,
  getDashboardLayout,
  saveDashboardLayout,
  resetDashboardLayout,
  addDashboardPanel,
  updateDashboardPanel,
  removeDashboardPanel,
  type DashboardPanel,
  type DashboardPanelInstance,
  type DashboardLayout,
  type PanelSize,
  type PanelPosition,
} from '../../api/platform';

// Re-export types
export type { DashboardPanel, DashboardPanelInstance, DashboardLayout, PanelSize, PanelPosition };

/**
 * Dashboard registry state
 */
interface DashboardRegistryState {
  // Panel definitions
  panels: DashboardPanel[];
  panelsByCategory: Record<string, DashboardPanel[]>;
  
  // User layout
  layout: DashboardLayout | null;
  
  // Panel components (registered locally by plugins)
  panelComponents: Map<string, React.ComponentType<any>>;
  
  // State
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // Edit mode
  isEditing: boolean;
  
  // Actions
  loadPanels: () => Promise<void>;
  loadLayout: () => Promise<void>;
  refresh: () => Promise<void>;
  saveLayout: (layout: Partial<DashboardLayout>) => Promise<void>;
  resetLayout: () => Promise<DashboardLayout>;
  addPanel: (panelId: string, config?: Record<string, any>) => Promise<DashboardPanelInstance>;
  updatePanel: (instanceId: string, updates: Partial<DashboardPanelInstance>) => Promise<void>;
  removePanel: (instanceId: string) => Promise<void>;
  movePanel: (instanceId: string, position: PanelPosition) => Promise<void>;
  resizePanel: (instanceId: string, size: PanelSize) => Promise<void>;
  registerComponent: (panelId: string, component: React.ComponentType<any>) => void;
  unregisterComponent: (panelId: string) => void;
  getComponent: (panelId: string) => React.ComponentType<any> | undefined;
  getPanel: (panelId: string) => DashboardPanel | undefined;
  getAvailablePanels: (userPermissions: string[]) => DashboardPanel[];
  setEditing: (editing: boolean) => void;
  reset: () => void;
}

/**
 * Check if user has required permissions
 */
function hasPermissions(requiredPermissions: string[] | undefined, userPermissions: string[]): boolean {
  if (!requiredPermissions?.length) return true;
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  if (userPermissions.includes('admin')) return true;
  return requiredPermissions.every(p => userPermissions.includes(p));
}

/**
 * Dashboard registry store
 */
export const useDashboardRegistry = create<DashboardRegistryState>()(
  subscribeWithSelector((set, get) => ({
    panels: [],
    panelsByCategory: {},
    layout: null,
    panelComponents: new Map(),
    isLoading: false,
    isSaving: false,
    error: null,
    lastUpdated: null,
    isEditing: false,

    loadPanels: async () => {
      const { isLoading } = get();
      if (isLoading) return;

      set({ isLoading: true, error: null });
      
      try {
        const data = await getDashboardPanels();
        set({
          panels: data.panels || [],
          panelsByCategory: data.byCategory || {},
          isLoading: false,
        });
      } catch (error) {
        set({
          panels: [],
          panelsByCategory: {},
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load panels',
        });
      }
    },

    loadLayout: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const layout = await getDashboardLayout();
        set({
          layout: {
            ...layout,
            panels: layout.panels || [],
          },
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (error) {
        set({
          layout: {
            userId: '',
            panels: [],
            columns: 12,
            rowHeight: 100,
            compactType: null,
            updatedAt: new Date().toISOString(),
          },
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load layout',
        });
      }
    },

    refresh: async () => {
      set({ isLoading: true, error: null });
      
      try {
        const [panelsData, layout] = await Promise.all([
          getDashboardPanels(),
          getDashboardLayout(),
        ]);
        
        set({
          panels: panelsData.panels || [],
          panelsByCategory: panelsData.byCategory || {},
          layout: {
            ...layout,
            panels: layout.panels || [],
          },
          isLoading: false,
          lastUpdated: new Date(),
        });
      } catch (error) {
        set({
          panels: [],
          panelsByCategory: {},
          layout: {
            userId: '',
            panels: [],
            columns: 12,
            rowHeight: 100,
            compactType: null,
            updatedAt: new Date().toISOString(),
          },
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to refresh dashboard',
        });
      }
    },

    saveLayout: async (layoutUpdates: Partial<DashboardLayout>) => {
      set({ isSaving: true, error: null });
      
      try {
        await saveDashboardLayout(layoutUpdates);
        
        // Update local state
        const { layout } = get();
        if (layout) {
          set({
            layout: { ...layout, ...layoutUpdates, updatedAt: new Date().toISOString() },
            isSaving: false,
          });
        }
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to save layout',
        });
      }
    },

    resetLayout: async () => {
      set({ isSaving: true, error: null });
      
      try {
        const layout = await resetDashboardLayout();
        set({
          layout,
          isSaving: false,
          lastUpdated: new Date(),
        });
        return layout;
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to reset layout',
        });
        throw error;
      }
    },

    addPanel: async (panelId: string, config?: Record<string, any>) => {
      set({ isSaving: true, error: null });
      
      try {
        const instance = await addDashboardPanel(panelId, config);
        
        // Update local layout
        const { layout } = get();
        if (layout) {
          set({
            layout: {
              ...layout,
              panels: [...layout.panels, instance],
              updatedAt: new Date().toISOString(),
            },
            isSaving: false,
          });
        }
        
        return instance;
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to add panel',
        });
        throw error;
      }
    },

    updatePanel: async (instanceId: string, updates: Partial<DashboardPanelInstance>) => {
      set({ isSaving: true, error: null });
      
      try {
        await updateDashboardPanel(instanceId, updates);
        
        // Update local layout
        const { layout } = get();
        if (layout) {
          set({
            layout: {
              ...layout,
              panels: layout.panels.map(p => 
                p.id === instanceId ? { ...p, ...updates } : p
              ),
              updatedAt: new Date().toISOString(),
            },
            isSaving: false,
          });
        }
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to update panel',
        });
      }
    },

    removePanel: async (instanceId: string) => {
      set({ isSaving: true, error: null });
      
      try {
        await removeDashboardPanel(instanceId);
        
        // Update local layout
        const { layout } = get();
        if (layout) {
          set({
            layout: {
              ...layout,
              panels: layout.panels.filter(p => p.id !== instanceId),
              updatedAt: new Date().toISOString(),
            },
            isSaving: false,
          });
        }
      } catch (error) {
        set({
          isSaving: false,
          error: error instanceof Error ? error.message : 'Failed to remove panel',
        });
      }
    },

    movePanel: async (instanceId: string, position: PanelPosition) => {
      await get().updatePanel(instanceId, { position });
    },

    resizePanel: async (instanceId: string, size: PanelSize) => {
      await get().updatePanel(instanceId, { size });
    },

    registerComponent: (panelId: string, component: React.ComponentType<any>) => {
      set({
        panelComponents: new Map(get().panelComponents).set(panelId, component),
      });
    },

    unregisterComponent: (panelId: string) => {
      const components = new Map(get().panelComponents);
      components.delete(panelId);
      set({ panelComponents: components });
    },

    getComponent: (panelId: string) => {
      return get().panelComponents.get(panelId);
    },

    getPanel: (panelId: string) => {
      const panels = get().panels;
      if (!panels || !Array.isArray(panels)) return undefined;
      return panels.find(p => `${p.pluginId}:${p.id}` === panelId || p.id === panelId);
    },

    getAvailablePanels: (userPermissions: string[]) => {
      const panels = get().panels;
      if (!panels || !Array.isArray(panels)) return [];
      if (!userPermissions || !Array.isArray(userPermissions)) return [];
      
      return panels.filter(panel => 
        panel.enabled && hasPermissions(panel.requiredPermissions, userPermissions)
      );
    },

    setEditing: (editing: boolean) => {
      set({ isEditing: editing });
    },

    reset: () => {
      set({
        panels: [],
        panelsByCategory: {},
        layout: null,
        isLoading: false,
        isSaving: false,
        error: null,
        lastUpdated: null,
        isEditing: false,
      });
    },
  }))
);

/**
 * Hook to use dashboard layout
 */
export function useDashboardLayout() {
  const {
    layout,
    panels,
    isLoading,
    isSaving,
    error,
    isEditing,
    loadLayout,
    loadPanels,
    refresh,
    saveLayout,
    resetLayout,
    addPanel,
    updatePanel,
    removePanel,
    movePanel,
    resizePanel,
    setEditing,
    getComponent,
    getPanel,
  } = useDashboardRegistry();

  // Load on mount
  React.useEffect(() => {
    if (!isLoading && !layout) {
      loadPanels();
      loadLayout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    layout,
    panels,
    isLoading,
    isSaving,
    error,
    isEditing,
    saveLayout,
    resetLayout,
    addPanel,
    updatePanel,
    removePanel,
    movePanel,
    resizePanel,
    setEditing,
    getComponent,
    getPanel,
    refresh,
  };
}

/**
 * Hook to get available panels for adding
 */
export function useAvailableDashboardPanels(userPermissions: string[] = []) {
  const { panels, panelsByCategory, isLoading, loadPanels, getAvailablePanels } = useDashboardRegistry();
  
  // Load on mount
  React.useEffect(() => {
    if (!isLoading && (!panels || panels.length === 0)) {
      loadPanels();
    }
  }, []);
  
  return {
    panels: getAvailablePanels(userPermissions),
    panelsByCategory,
    isLoading,
  };
}
